import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { Container, GlassCard } from "@/components/site/Primitives";
import { OnThisPageNav } from "@/components/site/OnThisPageNav";
import { CategoryPill } from "@/components/site/CategoryPill";
import { ArticleDocBody } from "@/components/doc/ArticleDocBody";
import { loadStoryBySlug, loadStories } from "@/lib/content-loader";
import { extractPageHeadings } from "@/lib/craft/headings";
import { pageUrl } from "@/lib/seo/meta";

export const Route = createFileRoute("/client-stories/$slug")({
  loader: async ({ params }) => {
    const [story, stories] = await Promise.all([
      loadStoryBySlug(params.slug),
      loadStories(),
    ]);
    if (!story) throw notFound();
    return { story, related: stories.filter((s) => s.slug !== params.slug) };
  },
  head: ({ loaderData, params }) => {
    const s = loaderData?.story;
    const storyUrl = pageUrl(`/client-stories/${params.slug}`);
    const title = s ? `${s.title} — KS Lau & Co Client Story` : "Client Story — KS Lau & Co";
    const desc = s?.excerpt ?? "Client story from KS Lau & Co.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:image", content: s?.cover },
        { property: "og:url", content: storyUrl },
        { name: "twitter:image", content: s?.cover },
      ],
      scripts: s
        ? [{
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: s.title,
              image: s.cover,
              datePublished: s.date,
              author: { "@type": "Organization", name: "KS Lau & Co" },
            }),
          }]
        : [],
    };
  },
  component: ClientStoryPage,
  notFoundComponent: () => (
    <div className="container-page py-40 text-center">
      <h1 className="text-2xl font-bold text-primary">Story not found</h1>
      <Link to="/client-stories" className="mt-4 inline-block text-base font-semibold text-primary hover:underline">
        ← All client stories
      </Link>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="container-page py-40 text-center">
      <h1 className="text-2xl font-bold text-primary">Something went wrong</h1>
      <button onClick={reset} className="mt-4 text-base font-semibold text-primary hover:underline">Try again</button>
    </div>
  ),
});

function ClientStoryPage() {
  const { story, related } = Route.useLoaderData();
  const craftState = (story as { craftState?: unknown }).craftState;
  const editorDoc = (story as { editorDoc?: unknown }).editorDoc;
  const pageHeadings = extractPageHeadings({
    editorDoc,
    craftState,
    body: story.body,
  });

  return (
    <>
      <section className="relative isolate overflow-hidden pb-10 pt-16 md:pb-12 md:pt-20">
        <div className="aurora">
          <div className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute right-0 top-20 h-[420px] w-[420px] rounded-full bg-primary/5 blur-3xl" />
        </div>
        <Container>
          <Link to="/client-stories" className="inline-flex items-center gap-2 text-base font-semibold text-primary hover:underline">
            <ArrowLeft className="h-4 w-4" /> All client stories
          </Link>
          <div className="mt-6 grid items-center gap-10 lg:grid-cols-[1.2fr_1fr]">
            <div className="max-w-3xl">
              <CategoryPill label={story.industry} size="sm" />
              <h1 className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-primary md:text-5xl">
                {story.title}
              </h1>
              <p className="mt-5 text-lg leading-relaxed text-foreground/80 md:text-lg">
                {story.excerpt}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-base text-muted-foreground">
                <span>{story.client}</span>
                <span aria-hidden="true">·</span>
                <span>{story.service}</span>
                <span aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {story.readTime}</span>
              </div>
            </div>
            <figure className="overflow-hidden rounded-xl ring-1 ring-primary/10 shadow-[0_30px_80px_-30px_rgb(32_56_112/0.45)]">
              <img
                src={story.cover}
                alt={story.title}
                width={1600}
                height={900}
                className="aspect-[16/10] w-full object-cover"
              />
            </figure>
          </div>

          {story.outcomes?.length ? (
            <dl className="mt-10 grid gap-6 py-6 sm:grid-cols-3">
              {story.outcomes.map((o) => (
                <div key={o.label}>
                  <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {o.label}
                  </dt>
                  <dd className="mt-1 text-2xl font-semibold tracking-tight text-primary md:text-3xl">
                    {o.value}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
        </Container>
      </section>

      <section className="relative pb-20 md:pb-28">
        <Container>
          <div className={`grid gap-10${pageHeadings.length ? " lg:grid-cols-[1fr_3fr]" : ""}`}>
            {pageHeadings.length > 0 ? <OnThisPageNav headings={pageHeadings} /> : null}

            <article >
              <ArticleDocBody
                editorDoc={editorDoc}
                craftState={craftState}
                craftCustomCss={(story as { craftCustomCss?: string }).craftCustomCss}
                body={story.body}
              />
            </article>
          </div>

          <div className="mx-auto mt-16 max-w-3xl">
            <GlassCard hover={false} className="p-8 md:p-10">
              <div className="grid items-center gap-5 md:grid-cols-[1.5fr_1fr]">
                <div>
                  <h3 className="text-xl font-bold tracking-tight text-primary">
                    Facing a similar challenge?
                  </h3>
                  <p className="mt-2 text-base text-muted-foreground">
                    Speak with our partners about how a focused review could apply to your business.
                  </p>
                </div>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 text-base font-semibold text-primary-foreground hover:bg-primary/90 md:justify-self-end">
                  Make an enquiry <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </GlassCard>
          </div>

          {related.length > 0 && (
            <div className="mx-auto mt-16 max-w-5xl">
              <h2 className="text-xl font-bold tracking-tight text-primary">More client stories</h2>
              <div className="mt-6 grid gap-5 md:grid-cols-2">
                {related.map((s) => (
                  <Link key={s.slug} to="/client-stories/$slug" params={{ slug: s.slug }} className="group">
                    <GlassCard className="flex h-full flex-col overflow-hidden p-0">
                      <img
                        src={s.cover}
                        alt={s.title}
                        loading="lazy"
                        width={1600}
                        height={900}
                        className="aspect-[16/9] w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                      />
                      <div className="p-6">
                        <CategoryPill label={s.industry} />
                        <h3 className="mt-2 text-lg font-bold tracking-tight text-primary">{s.title}</h3>
                        <p className="mt-2 text-base leading-relaxed text-muted-foreground">{s.excerpt}</p>
                      </div>
                    </GlassCard>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </Container>
      </section>
    </>
  );
}
